import * as React from "react";
import {
	Alert,
	Autocomplete,
	Button,
	FormControlLabel,
	MenuItem,
	Stack,
	Switch,
	TextField,
	Typography,
} from "@mui/material";

import type { CalendarSimulation } from "@/hooks/api/use-proving-ground";

const TIMEZONES = Intl.supportedValuesOf("timeZone");
const WORKING_DAYS = /^[1-7](,[1-7])*$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const defaults = (): CalendarSimulation => ({
	clock: new Date().toISOString(),
	settings: {
		calendarId: "simulation",
		timezone: "Asia/Kuala_Lumpur",
		accessMode: "book",
		workingDays: "1,2,3,4,5",
		openingTime: "09:00",
		closingTime: "17:00",
		durationMinutes: 30,
		slotIntervalMinutes: 15,
		bufferMinutes: 0,
		noticeMinutes: 60,
		horizonDays: 60,
		sendInvitations: true,
	},
	busy: [],
	availability: "available",
	bookingOutcome: "confirmed",
	expected: { count: 1, status: "confirmed", attendees: [] },
});

function ClockField({
	label,
	value,
	helperText,
	error,
	onChange,
}: {
	label: string;
	value?: string;
	helperText?: string;
	error?: boolean;
	onChange: (value?: string) => void;
}) {
	return (
		<TextField
			label={`${label} (UTC)`}
			type="datetime-local"
			fullWidth
			error={error}
			helperText={helperText}
			value={value ? new Date(value).toISOString().slice(0, 16) : ""}
			slotProps={{ inputLabel: { shrink: true } }}
			onChange={(e) => {
				if (e.target.value) {
					const date = new Date(`${e.target.value}Z`);
					if (Number.isFinite(date.getTime())) onChange(date.toISOString());
				} else {
					onChange();
				}
			}}
		/>
	);
}

/**
 * Whole minutes inside the range the backend accepts. The typed text is kept locally so a
 * half-typed or cleared field stays as the author left it instead of snapping to a number the
 * backend would reject anyway.
 */
function NumberField({
	label,
	value,
	min,
	max,
	optional,
	helperText,
	onChange,
}: {
	label: string;
	value?: number;
	min: number;
	max: number;
	optional?: boolean;
	helperText?: string;
	onChange: (value?: number) => void;
}) {
	const [text, setText] = React.useState(value === undefined ? "" : String(value));
	React.useEffect(() => setText(value === undefined ? "" : String(value)), [value]);
	const blank = text.trim() === "";
	const valid = /^\d+$/.test(text.trim()) && Number(text) >= min && Number(text) <= max;
	const error = optional ? !blank && !valid : !valid;
	return (
		<TextField
			label={label}
			type="number"
			fullWidth
			value={text}
			error={error}
			helperText={error ? `Use a whole number between ${min} and ${max}.` : helperText}
			slotProps={{ htmlInput: { min, max, step: 1 } }}
			onChange={(e) => {
				setText(e.target.value);
				const typed = e.target.value.trim();
				if (optional && typed === "") onChange();
				else if (/^\d+$/.test(typed) && Number(typed) >= min && Number(typed) <= max) onChange(Number(typed));
			}}
		/>
	);
}

function AttendeeField({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
	const joined = value.join(", ");
	const [text, setText] = React.useState(joined);
	React.useEffect(() => setText(joined), [joined]);
	const entries = text
		.split(",")
		.map((v) => v.trim())
		.filter(Boolean);
	const invalid = entries.filter((entry) => !EMAIL.test(entry));
	return (
		<TextField
			label="Expected attendee emails"
			value={text}
			error={invalid.length > 0}
			onChange={(e) => setText(e.target.value)}
			onBlur={() => onChange(invalid.length > 0 ? value : entries)}
			helperText={
				invalid.length > 0
					? `Not an email address: ${invalid.join(", ")}`
					: "Comma-separated. Leave blank to require a booking without an invitation."
			}
		/>
	);
}

export function CalendarEditor({
	value,
	onChange,
}: {
	value: CalendarSimulation | null;
	onChange: (value: CalendarSimulation | null) => void;
}) {
	const update = (patch: Partial<CalendarSimulation>) => value && onChange({ ...value, ...patch });
	const rule = (patch: Partial<CalendarSimulation["settings"]>) =>
		value && update({ settings: { ...value.settings, ...patch } });
	const expected = (patch: Partial<CalendarSimulation["expected"]>) =>
		value && update({ expected: { ...value.expected, ...patch } });
	const days = value?.settings.workingDays ?? "";
	const opens = value?.settings.openingTime ?? "";
	const closes = value?.settings.closingTime ?? "";
	const hours = TIME.test(opens) && TIME.test(closes) && opens >= closes;
	return (
		<Stack spacing={2}>
			<FormControlLabel
				control={<Switch checked={Boolean(value)} onChange={(_, checked) => onChange(checked ? defaults() : null)} />}
				label="Simulate calendar bookings"
			/>
			{value ? (
				<>
					<Alert severity="info">
						Each call gets its own test calendar. No Google events or invitations are sent. Match these rules to the
						agent&apos;s Calendar service; add a Calendar booking outcome check to the scorecard.
					</Alert>
					<ClockField
						label="Scenario clock"
						value={value.clock}
						helperText="The call starts at this moment; the simulated clock does not move."
						onChange={(clock) => clock && update({ clock })}
					/>
					<Autocomplete
						options={TIMEZONES}
						value={value.settings.timezone}
						disableClearable
						onChange={(_event, timezone) => timezone && rule({ timezone })}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Calendar timezone"
								helperText="Relative dates use the scenario clock in this timezone."
							/>
						)}
					/>
					<Stack direction="row" spacing={1}>
						<TextField
							label="Opens"
							type="time"
							value={opens}
							error={!TIME.test(opens) || hours}
							helperText={hours ? "Closing time must follow opening time." : undefined}
							onChange={(e) => rule({ openingTime: e.target.value })}
							fullWidth
						/>
						<TextField
							label="Closes"
							type="time"
							value={closes}
							error={!TIME.test(closes) || hours}
							onChange={(e) => rule({ closingTime: e.target.value })}
							fullWidth
						/>
						<NumberField
							label="Duration (minutes)"
							value={value.settings.durationMinutes}
							min={5}
							max={480}
							onChange={(durationMinutes) => durationMinutes && rule({ durationMinutes })}
						/>
					</Stack>
					<TextField
						label="Working days"
						value={days}
						error={!WORKING_DAYS.test(days)}
						onChange={(e) => rule({ workingDays: e.target.value })}
						helperText="Monday = 1, Sunday = 7. Separate days with commas, e.g. 1,2,3,4,5."
					/>
					<Stack direction="row" spacing={1}>
						<NumberField
							label="Slot interval (minutes)"
							value={value.settings.slotIntervalMinutes}
							min={5}
							max={120}
							onChange={(slotIntervalMinutes) => slotIntervalMinutes && rule({ slotIntervalMinutes })}
						/>
						<NumberField
							label="Buffer (minutes)"
							value={value.settings.bufferMinutes}
							min={0}
							max={120}
							onChange={(bufferMinutes) => bufferMinutes !== undefined && rule({ bufferMinutes })}
						/>
					</Stack>
					<Stack direction="row" spacing={1}>
						<NumberField
							label="Minimum notice (minutes)"
							value={value.settings.noticeMinutes}
							min={0}
							max={10_080}
							onChange={(noticeMinutes) => noticeMinutes !== undefined && rule({ noticeMinutes })}
						/>
						<NumberField
							label="Booking horizon (days)"
							value={value.settings.horizonDays}
							min={1}
							max={180}
							onChange={(horizonDays) => horizonDays && rule({ horizonDays })}
						/>
					</Stack>
					<TextField
						select
						label="Availability response"
						value={value.availability}
						onChange={(e) => update({ availability: e.target.value as CalendarSimulation["availability"] })}
					>
						<MenuItem value="available">Use calendar rules and busy times</MenuItem>
						<MenuItem value="unavailable">Calendar service unavailable</MenuItem>
					</TextField>
					<TextField
						select
						label="Booking response"
						value={value.bookingOutcome}
						onChange={(e) => update({ bookingOutcome: e.target.value as CalendarSimulation["bookingOutcome"] })}
					>
						<MenuItem value="confirmed">Confirmed</MenuItem>
						<MenuItem value="confirmation_pending">Confirmation pending</MenuItem>
						<MenuItem value="conflict">Slot taken before booking</MenuItem>
						<MenuItem value="unavailable">Calendar service unavailable</MenuItem>
					</TextField>
					<Typography variant="subtitle2">Busy times</Typography>
					{value.busy.map((busy, i) => {
						const backwards = Date.parse(busy.end) <= Date.parse(busy.start);
						return (
							<Stack spacing={1} key={i}>
								<Stack direction="row" spacing={1}>
									<ClockField
										label="Busy from"
										value={busy.start}
										error={backwards}
										onChange={(start) =>
											start && update({ busy: value.busy.map((b, n) => (n === i ? { ...b, start } : b)) })
										}
									/>
									<ClockField
										label="Busy until"
										value={busy.end}
										error={backwards}
										helperText={backwards ? "A busy time must end after it starts." : undefined}
										onChange={(end) => end && update({ busy: value.busy.map((b, n) => (n === i ? { ...b, end } : b)) })}
									/>
								</Stack>
								<Button size="small" onClick={() => update({ busy: value.busy.filter((_, n) => n !== i) })}>
									Remove busy time
								</Button>
							</Stack>
						);
					})}
					<Button
						disabled={value.busy.length >= 100}
						onClick={() =>
							update({
								busy: [
									...value.busy,
									{ start: value.clock, end: new Date(Date.parse(value.clock) + 3_600_000).toISOString() },
								],
							})
						}
					>
						Add busy time
					</Button>
					<Typography variant="subtitle2">Expected booking result</Typography>
					<Stack direction="row" spacing={1}>
						<NumberField
							label="Number of bookings"
							value={value.expected.count}
							min={0}
							max={10}
							onChange={(count) => count !== undefined && expected({ count })}
						/>
						<NumberField
							label="Booking attempts (optional)"
							value={value.expected.attempts}
							min={0}
							max={20}
							optional
							onChange={(attempts) => expected({ attempts })}
						/>
					</Stack>
					{value.expected.count > 0 ? (
						<>
							<TextField
								select
								label="Expected status"
								value={value.expected.status ?? "confirmed"}
								onChange={(e) => expected({ status: e.target.value as "confirmed" | "confirmation_pending" })}
							>
								<MenuItem value="confirmed">Confirmed</MenuItem>
								<MenuItem value="confirmation_pending">Confirmation pending</MenuItem>
							</TextField>
							<ClockField
								label="Expected start (optional)"
								value={value.expected.start}
								onChange={(start) => expected({ start })}
							/>
							<ClockField
								label="Expected end (optional)"
								value={value.expected.end}
								onChange={(end) => expected({ end })}
							/>
							<AttendeeField value={value.expected.attendees ?? []} onChange={(attendees) => expected({ attendees })} />
						</>
					) : null}
				</>
			) : null}
		</Stack>
	);
}
